/**
 * INBX UI primitives.
 * Спецификация: gdd/ui/ui-00-global-components.md.
 */
import { type ReactNode, useEffect, useState } from 'react';

import './ui.css';

// ── Status / Priority chips ────────────────────────────────────────────

export type TicketStatus =
  | 'new'
  | 'bot_handling'
  | 'queued'
  | 'open'
  | 'pending'
  | 'escalated'
  | 'resolved'
  | 'closed';

const STATUS_LABELS: Record<TicketStatus, string> = {
  new: 'Новый',
  bot_handling: 'У бота',
  queued: 'В очереди',
  open: 'Открыт',
  pending: 'Ожидание',
  escalated: 'Эскалация',
  resolved: 'Решён',
  closed: 'Закрыт',
};

export function StatusChip({
  status,
  size = 'md',
}: {
  status: TicketStatus;
  size?: 'sm' | 'md';
}): JSX.Element {
  return (
    <span className={`inbx-chip inbx-chip--status inbx-chip--${status} inbx-chip--${size}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

export function PriorityBar({ priority }: { priority: TicketPriority }): JSX.Element {
  return <span className={`inbx-priority-bar inbx-priority-bar--${priority}`} />;
}

const PRIORITY_LABELS: Record<TicketPriority, string> = {
  urgent: 'Срочно',
  high: 'Высокий',
  medium: 'Средний',
  low: 'Низкий',
};

export function PriorityChip({ priority }: { priority: TicketPriority }): JSX.Element {
  return (
    <span className={`inbx-chip inbx-chip--priority inbx-chip--priority-${priority}`}>
      <span className={`inbx-priority-dot inbx-priority-dot--${priority}`} />
      {PRIORITY_LABELS[priority]}
    </span>
  );
}

// ── Channel ──────────────────────────────────────────────────────────

export type ChannelType =
  | 'telegram'
  | 'whatsapp'
  | 'vk'
  | 'email'
  | 'chat'
  | 'wildberries'
  | 'ozon'
  | 'avito'
  | 'sms'
  | 'partner'
  | 'instagram'
  | 'facebook'
  | 'custom';

const CHANNEL_META: Record<ChannelType, { icon: string; label: string }> = {
  telegram: { icon: 'ti-brand-telegram', label: 'Telegram' },
  whatsapp: { icon: 'ti-brand-whatsapp', label: 'WhatsApp' },
  vk: { icon: 'ti-brand-vk', label: 'ВКонтакте' },
  email: { icon: 'ti-mail', label: 'Email' },
  chat: { icon: 'ti-message-circle', label: 'Чат на сайте' },
  wildberries: { icon: 'ti-star', label: 'Wildberries' },
  ozon: { icon: 'ti-shopping-cart', label: 'Ozon' },
  avito: { icon: 'ti-home', label: 'Avito' },
  sms: { icon: 'ti-message-2', label: 'SMS' },
  partner: { icon: 'ti-building-store', label: 'Канал магазина' },
  instagram: { icon: 'ti-brand-instagram', label: 'Instagram' },
  facebook: { icon: 'ti-brand-facebook', label: 'Facebook' },
  custom: { icon: 'ti-puzzle', label: 'Custom' },
};

export function ChannelIcon({
  channel,
  size = 20,
}: {
  channel: ChannelType;
  size?: number;
}): JSX.Element {
  const meta = CHANNEL_META[channel] ?? CHANNEL_META.custom;
  return (
    <span
      className={`inbx-channel-icon inbx-channel-icon--${channel}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.65) }}
      title={meta.label}
    >
      <i className={meta.icon} />
    </span>
  );
}

export function channelLabel(channel: ChannelType): string {
  return (CHANNEL_META[channel] ?? CHANNEL_META.custom).label;
}

// ── Avatars ──────────────────────────────────────────────────────────

const AVATAR_BG_COUNT = 6;
function avatarPaletteIndex(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return Math.abs(h) % AVATAR_BG_COUNT;
}
function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function OperatorAvatar({
  user,
  size = 'md',
  status,
}: {
  user: { id: string; name: string; avatarUrl?: string | null };
  size?: 'xs' | 'sm' | 'md' | 'lg';
  status?: 'online' | 'busy' | 'away' | 'offline';
}): JSX.Element {
  const idx = avatarPaletteIndex(user.id);
  const px = { xs: 18, sm: 22, md: 30, lg: 44 }[size];
  return (
    <span
      className={`inbx-avatar inbx-avatar--${size} inbx-avatar--bg-${idx + 1}`}
      style={{ width: px, height: px, fontSize: Math.round(px * 0.4) }}
    >
      {user.avatarUrl ? <img src={user.avatarUrl} alt={user.name} /> : initials(user.name)}
      {status && <span className={`inbx-avatar__status inbx-avatar__status--${status}`} />}
    </span>
  );
}

// ── Dots / indicators ────────────────────────────────────────────────

export function UnreadDot(): JSX.Element {
  return <span className="inbx-unread-dot" aria-label="unread" />;
}

// ── SLA timer ────────────────────────────────────────────────────────

function fmtDuration(ms: number): string {
  const totalMin = Math.round(ms / 60_000);
  if (totalMin >= 60) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m === 0 ? `${h}ч` : `${h}ч ${m}мин`;
  }
  return `${totalMin}мин`;
}

export function SLATimer({
  deadline,
  breached,
}: {
  deadline: string | Date | null | undefined;
  breached?: boolean;
}): JSX.Element {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((v) => v + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!deadline) return <span className="inbx-sla inbx-sla--none">—</span>;
  const d = typeof deadline === 'string' ? new Date(deadline) : deadline;
  const diff = d.getTime() - Date.now();

  if (breached || diff <= 0) {
    return (
      <span className="inbx-sla inbx-sla--breached">
        <i className="ti-urgent" /> Нарушен
      </span>
    );
  }
  const minutes = diff / 60_000;
  let cls = 'inbx-sla--ok';
  let icon: ReactNode = null;
  if (minutes < 30) {
    cls = 'inbx-sla--burn';
    icon = <i className="ti-urgent" />;
  } else if (minutes <= 60) {
    cls = 'inbx-sla--warn';
  }
  return (
    <span className={`inbx-sla ${cls}`}>
      {icon}
      {fmtDuration(diff)}
    </span>
  );
}

// ── Chips / buttons ──────────────────────────────────────────────────

export function FilterChip({
  active,
  onClick,
  onRemove,
  icon,
  children,
}: {
  active?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  icon?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <button type="button" className={`inbx-fchip ${active ? 'is-active' : ''}`} onClick={onClick}>
      {icon}
      <span>{children}</span>
      {onRemove && (
        <span
          className="inbx-fchip__remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <i className="ti-x" />
        </span>
      )}
    </button>
  );
}

export function SavedViewTab({
  active,
  icon,
  label,
  onClick,
  onRemove,
}: {
  active?: boolean;
  icon?: string;
  label: string;
  onClick?: () => void;
  onRemove?: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`inbx-saved-tab ${active ? 'is-active' : ''}`}
      onClick={onClick}
    >
      {icon && <i className={icon} />}
      <span>{label}</span>
      {onRemove && (
        <span
          className="inbx-saved-tab__remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <i className="ti-x" />
        </span>
      )}
    </button>
  );
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  ...rest
}: {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  icon?: string;
  children?: ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
  return (
    <button
      type="button"
      {...rest}
      className={`inbx-btn inbx-btn--${variant} inbx-btn--${size} ${rest.className ?? ''}`}
    >
      {icon && <i className={icon} />}
      {children}
    </button>
  );
}

export function IconButton({
  icon,
  tooltip,
  ...rest
}: {
  icon: string;
  tooltip?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>): JSX.Element {
  return (
    <button
      type="button"
      {...rest}
      className={`inbx-icon-btn ${rest.className ?? ''}`}
      title={tooltip}
    >
      <i className={icon} />
    </button>
  );
}

// ── Empty / Loading / Skeleton ───────────────────────────────────────

export function EmptyState({
  icon = 'ti-inbox',
  title,
  description,
  action,
}: {
  icon?: string;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
}): JSX.Element {
  return (
    <div className="inbx-empty">
      <i className={`${icon} inbx-empty__icon`} />
      <div className="inbx-empty__title">{title}</div>
      {description && <div className="inbx-empty__desc">{description}</div>}
      {action && (
        <Button variant="primary" size="md" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

export function Skeleton({
  height = 12,
  width = '100%',
  radius = 4,
}: {
  height?: number;
  width?: number | string;
  radius?: number;
}): JSX.Element {
  return <span className="inbx-skeleton" style={{ height, width, borderRadius: radius }} />;
}

// ── Tags ──────────────────────────────────────────────────────────────

export function Tag({
  children,
  onRemove,
  color,
}: {
  children: ReactNode;
  onRemove?: () => void;
  color?: string;
}): JSX.Element {
  return (
    <span className="inbx-tag" style={color ? { background: color } : undefined}>
      {children}
      {onRemove && (
        <span className="inbx-tag__remove" onClick={onRemove}>
          <i className="ti-x" />
        </span>
      )}
    </span>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

export function timeAgo(ts: string | Date | null | undefined): string {
  if (!ts) return '—';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин назад`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} ч назад`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days} дн назад`;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export function formatDateTime(ts: string | Date | null | undefined): string {
  if (!ts) return '—';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatTime(ts: string | Date | null | undefined): string {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

export function formatDate(ts: string | Date | null | undefined): string {
  if (!ts) return '—';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ── PageHeader ────────────────────────────────────────────────────────

export function PageHeader({
  title,
  badge,
  tabs,
  actions,
  filters,
}: {
  title: string;
  badge?: { count: number };
  tabs?: { key: string; label: string; active?: boolean; onClick?: () => void }[];
  actions?: ReactNode;
  filters?: ReactNode;
}): JSX.Element {
  return (
    <div className="inbx-page-header">
      <div className="inbx-page-header__top">
        <h1 className="inbx-page-header__title">
          {title}
          {badge && <span className="inbx-page-header__badge">{badge.count}</span>}
        </h1>
        {actions && <div className="inbx-page-header__actions">{actions}</div>}
      </div>
      {tabs && (
        <div className="inbx-page-header__tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`inbx-page-header__tab ${t.active ? 'is-active' : ''}`}
              onClick={t.onClick}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      {filters && <div className="inbx-page-header__filters">{filters}</div>}
    </div>
  );
}

// ── DataTable ─────────────────────────────────────────────────────────

export interface Column<R> {
  key: string;
  label: string;
  width?: string | number;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  render: (row: R) => ReactNode;
}

export function DataTable<R extends { id: string }>({
  rows,
  columns,
  loading,
  selectable,
  selectedIds,
  onSelect,
  onRowClick,
  emptyState,
  sortKey,
  sortDir,
  onSort,
}: {
  rows: R[];
  columns: Column<R>[];
  loading?: boolean;
  selectable?: boolean;
  selectedIds?: Set<string>;
  onSelect?: (ids: Set<string>) => void;
  onRowClick?: (row: R) => void;
  emptyState?: ReactNode;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
}): JSX.Element {
  if (loading) {
    return (
      <table className="inbx-table">
        <thead>
          <tr>
            {selectable && <th className="inbx-table__cb" />}
            {columns.map((c) => (
              <th key={c.key} style={{ width: c.width }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }).map((_, i) => (
            <tr key={i}>
              {selectable && (
                <td>
                  <Skeleton height={14} width={14} />
                </td>
              )}
              {columns.map((c) => (
                <td key={c.key}>
                  <Skeleton height={12} width="80%" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (!rows || rows.length === 0) {
    return <>{emptyState ?? <EmptyState title="Нет данных" />}</>;
  }

  const allSelected =
    selectable && selectedIds && rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  return (
    <table className="inbx-table">
      <thead>
        <tr>
          {selectable && (
            <th className="inbx-table__cb">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={(e) => {
                  if (!onSelect) return;
                  const next = new Set(selectedIds);
                  if (e.target.checked) rows.forEach((r) => next.add(r.id));
                  else rows.forEach((r) => next.delete(r.id));
                  onSelect(next);
                }}
              />
            </th>
          )}
          {columns.map((c) => {
            const canSort = c.sortable && !!onSort;
            const arrow = sortKey === c.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
            return (
              <th
                key={c.key}
                style={{
                  width: c.width,
                  textAlign: c.align ?? 'left',
                  cursor: canSort ? 'pointer' : undefined,
                  userSelect: 'none',
                }}
                onClick={canSort ? () => onSort(c.key) : undefined}
                title={canSort ? 'Сортировать' : undefined}
              >
                {c.label}
                {canSort ? (arrow || ' ↕') : ''}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr
            key={row.id}
            className={selectedIds?.has(row.id) ? 'is-selected' : ''}
            onClick={() => onRowClick?.(row)}
            style={onRowClick ? { cursor: 'pointer' } : undefined}
          >
            {selectable && (
              <td className="inbx-table__cb" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds?.has(row.id) ?? false}
                  onChange={(e) => {
                    if (!onSelect) return;
                    const next = new Set(selectedIds);
                    if (e.target.checked) next.add(row.id);
                    else next.delete(row.id);
                    onSelect(next);
                  }}
                />
              </td>
            )}
            {columns.map((c) => (
              <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                {c.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── SegmentedControl ──────────────────────────────────────────────────

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}): JSX.Element {
  return (
    <div className="inbx-segmented">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`inbx-segmented__btn ${value === o.value ? 'is-active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── MeterBar ──────────────────────────────────────────────────────────

export function MeterBar({ pct, label }: { pct: number; label?: string }): JSX.Element {
  const p = Math.min(100, Math.max(0, pct));
  let cls = 'inbx-meter--ok';
  if (p > 80) cls = 'inbx-meter--danger';
  else if (p > 60) cls = 'inbx-meter--warn';
  return (
    <div className="inbx-meter">
      <div className={`inbx-meter__fill ${cls}`} style={{ width: `${p}%` }} />
      {label && <span className="inbx-meter__label">{label}</span>}
    </div>
  );
}

// ── DrawerPanel ───────────────────────────────────────────────────────

export function DrawerPanel({
  open,
  title,
  subtitle,
  onClose,
  footer,
  children,
  width = 420,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  width?: number;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="inbx-drawer-overlay" onClick={onClose} />
      <div className="inbx-drawer" style={{ width }}>
        <div className="inbx-drawer__header">
          <div>
            <h2 className="inbx-drawer__title">{title}</h2>
            {subtitle && <div className="inbx-drawer__sub">{subtitle}</div>}
          </div>
          <IconButton icon="ti-x" tooltip="Закрыть" onClick={onClose} />
        </div>
        <div className="inbx-drawer__body">{children}</div>
        {footer && <div className="inbx-drawer__footer">{footer}</div>}
      </div>
    </>
  );
}

// ── ConfirmModal ──────────────────────────────────────────────────────

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Подтвердить',
  variant = 'primary',
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  variant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  if (!open) return null;
  return (
    <>
      <div className="inbx-modal-overlay" onClick={onCancel} />
      <div className="inbx-modal">
        <h3 className="inbx-modal__title">{title}</h3>
        {description && <div className="inbx-modal__desc">{description}</div>}
        <div className="inbx-modal__actions">
          <Button variant="secondary" onClick={onCancel}>
            Отмена
          </Button>
          <Button variant={variant} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </>
  );
}

// ── Field / Label / Input helpers ─────────────────────────────────────

export function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="inbx-field">
      <span className="inbx-field__label">
        {label}
        {required && <span className="inbx-field__req"> *</span>}
      </span>
      {children}
      {hint && <span className="inbx-field__hint">{hint}</span>}
    </label>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return <input {...props} className={`inbx-input ${props.className ?? ''}`} />;
}
export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>): JSX.Element {
  return <textarea {...props} className={`inbx-input inbx-textarea ${props.className ?? ''}`} />;
}
export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>): JSX.Element {
  return <select {...props} className={`inbx-input inbx-select ${props.className ?? ''}`} />;
}

export function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}): JSX.Element {
  return (
    <label className="inbx-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="inbx-toggle__slider" />
      {label && <span className="inbx-toggle__label">{label}</span>}
    </label>
  );
}

// ── InlineTagEditor ────────────────────────────────────────────────

export function InlineTagEditor({
  value,
  onChange,
  placeholder = 'Добавить тег...',
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}): JSX.Element {
  const [text, setText] = useState('');
  function add(): void {
    const t = text.trim();
    if (t && !value.includes(t)) onChange([...value, t]);
    setText('');
  }
  return (
    <div className="inbx-tag-editor">
      {value.map((t) => (
        <Tag key={t} onRemove={() => onChange(value.filter((x) => x !== t))}>
          {t}
        </Tag>
      ))}
      <input
        className="inbx-tag-editor__input"
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
          if (e.key === 'Backspace' && !text && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={add}
      />
    </div>
  );
}

// ── RoleBadge ─────────────────────────────────────────────────────────

const ROLE_LABEL: Record<string, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  supervisor: 'Супервизор',
  operator: 'Оператор',
};
export function RoleBadge({ role }: { role: string }): JSX.Element {
  return (
    <span className={`inbx-role-badge inbx-role-badge--${role}`}>{ROLE_LABEL[role] ?? role}</span>
  );
}
